import {
  Component,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, take, takeUntil, finalize, forkJoin, of, Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ToastService } from '../../services/toast.service';
import { LaneAttentionService } from '../../services/lane-attention.service';
import { NotificationData } from '../../services/notification.service';
import { ModalStateService } from '../../services/modal-state.service';
import { WayPurchaseModalStateService } from '../way-purchase-modal/services/way-purchase-modal-state.service';
import { WayPurchaseApiService } from '../way-purchase-modal/services/way-purchase-api.service';
import { buildConfirmWayPurchaseRequest } from '../way-purchase-modal/utils/way-purchase-single-wash-display.util';
import { buildOrderSuccessNotificationFromConfirmWayResponse } from '../way-purchase-modal/utils/way-purchase-selection-handoff.util';
import type { WayNormalizedPurchase } from '../way-purchase-modal/models/way-purchase.models';
import { openPusherNotificationModal } from '../../shared/utils/notification-modal-open.util';
import { buildPrepaidRedeemHandoffNotification } from './utils/multi-eligible-prepaid-handoff.util';
import { extractErrorMessage } from '../../shared/utils/error.util';
import { VehicleService } from '../../services/vehicle.service';
import { PrepaidWashBookService } from '../../services/prepaid-wash-book.service';
import { LocationService } from '../../services/location.service';
import { EmployeeService } from '../../services/employee.service';
import { ApiService } from '../../services/api.service';
import { OrdersService } from '../../orders/services/orders.service';
import { getUserDataLaneId, getUserDataLocationId } from '../../shared/utils/auth-headers.util';
import { CUSTOMER_PHONE_MAX_DIGITS } from '../../shared/validators/customer-contact-field.rules';
import { isWayPurchaseLookupEnabled } from '../way-purchase-modal/utils/way-purchase-lookup-enabled.util';
import { MultiEligibleConfirmApiService } from './services/multi-eligible-confirm-api.service';
import {
  MultiEligibleSelectionModalState,
  MultiEligibleSelectionModalStateService,
} from './services/multi-eligible-selection-modal-state.service';
import {
  MultiEligibleModalMode,
  MultiEligibleOptionCardVm,
  MultiEligibleOptionCategoryVm,
  MultiEligiblePrepaidItem,
  MultiEligiblePurchaseOption,
  MultiEligibleSelectionKind,
  MultiEligibleVehicleCardVm,
  MultiEligibleWizardStep,
} from './models/multi-eligible.models';
import {
  attachPrepaidStatusItemsToOption,
  buildMultiEligibleConfirmRequest,
  buildMultiEligibleOptionCards,
  buildNotificationFromMultiEligibleConfirmResponse,
  groupMultiEligibleOptionCards,
  buildPhoneMembershipRedeemPayload,
  buildPhoneSingleWashRedeemPayload,
  buildPrepaidRedeemOrderSuccessDisplayContext,
  buildPrepaidWashbookTitle,
  buildVehicleCardsFromPhoneResponse,
  isMembershipRedeemEvent,
  isWaySingleWashOption,
  listRedeemablePrepaidItems,
  mapWayPurchaseToMultiEligibleOption,
  optionIsPrepaidPurchase,
  readMultiEligiblePurchaseOptions,
  resolveMultiEligibleChooserLabel,
  resolveMultiEligibleCustomerName,
  resolveMultiEligibleCustomerPhone,
  resolveMultiEligibleModalTitle,
  resolveMultiEligiblePlateDisplay,
  resolveMultiEligiblePlateState,
  resolveMultiEligibleSelectionKind,
  stripMultiEligiblePlateState,
} from './utils/multi-eligible-confirm.util';
import type { PrepaidPurchaseStatus } from '../../models/prepaid-wash-book.models';
import { VehiclesWithMembershipsResponse } from '../../services/vehicle.service';
import {
  expandPrepaidPurchaseStatusToRedemptionSlots,
  formatPrepaidRedemptionSlotSubline,
  getPrepaidPurchaseStatusSummaryCards,
  isPrepaidPurchaseFullyRedeemed,
  mapPrepaidPurchaseRedemptionsToSlots,
  type PrepaidPurchaseStatusSummaryCard,
  type PrepaidRedemptionSlot,
  type PrepaidRedemptionSlotTab,
} from '../../models/prepaid-purchase-status.util';
import { WashHistoryPanelComponent } from '../notification-modal/components/wash-history-panel/wash-history-panel.component';
import { PlanChangePanelComponent } from '../notification-modal/components/plan-change-panel/plan-change-panel.component';
import { PlanChangeSettingsService } from '../../services/plan-change-settings.service';
import { RoleService } from '../../services/role.service';
import { PlanChangeResult, buildPlanChangeNotice } from '../../services/plan-change.service';
import { RewashReasonModalComponent } from '../notification-modal/components/rewash-reason-modal/rewash-reason-modal.component';
import {
  MembershipRewashEligibilityState,
  PrepaidRewashEligibilityState,
  OrderRequestRewashTarget,
  OrderRequestTodayBannerState,
  RewashEligibilityState,
} from '../notification-modal/models/notification-order-request.models';
import { NotificationOrderRequestHistoryService } from '../notification-modal/services/notification-order-request-history.service';
import {
  buildOrderRequestHistoryInsight,
  normalizeRewashTarget,
  parseMembershipRewashEligibilityFromHistoryResponse,
  parsePrepaidRewashEligibilityFromHistoryResponse,
  parseRewashEligibilityFromHistoryResponse,
  patchOrderRequestHistoryBannerPage,
} from '../notification-modal/utils/notification-order-request-history.util';
import type { CreateRewashOrderResult } from '../../services/rewash-reason.service';
import {
  CustomerSubscription,
  CustomerSubscriptionService,
} from '../../services/customer-subscription.service';
import { SubscriptionPauseActionsService } from '../../services/subscription-pause-actions.service';
import {
  SavedCard,
  SubscriptionPaymentMethodService,
} from '../../services/subscription-payment-method.service';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal/confirm-action-modal.component';
import { ManageSubscriptionMenuComponent } from '../../shared/components/manage-subscription-menu/manage-subscription-menu.component';
import { PauseSubscriptionModalComponent } from '../../shared/components/pause-subscription-modal/pause-subscription-modal.component';
import {
  PaymentMethodModalComponent,
  PaymentMethodSubject,
} from '../../shared/components/payment-method-modal/payment-method-modal.component';
import {
  cardWithExpiryLabel,
  renewalCardUpdatedToast,
} from '../../shared/utils/payment-method.copy';
import { importedBilledBy } from '../../shared/utils/payment-method-access.util';
import { SubscriptionPauseNoticeComponent } from '../../shared/components/subscription-pause-notice/subscription-pause-notice.component';
import { FamilyMembersModalComponent } from '../../shared/components/family-members-modal/family-members-modal.component';
import { StartFamilyModalComponent } from '../../shared/components/start-family-modal/start-family-modal.component';
import { SubscriptionResultModalComponent } from '../../shared/components/subscription-result-modal/subscription-result-modal.component';
import type {
  PauseOutcome,
  PauseSubject,
  SubscriptionPause,
} from '../../shared/types/subscription-pause.types';
import { FAMILY_PRIMARY_TAG_LABEL, FAMILY_TAG_LABEL } from '../../shared/utils/family-tags.util';
import { PauseFlowController } from '../../shared/utils/pause-flow.controller';
import { FamilyMembersFlowController } from '../../shared/utils/family-members-flow.controller';
import { StartFamilyFlowController } from '../../shared/utils/start-family-flow.controller';
import { FamilyMembersService } from '../../services/family-members.service';
import { FamilyStartService } from '../../services/family-start.service';
import { activateConfirmCopy, pauseRefusalCopy } from '../../shared/utils/subscription-pause.copy';
import { PauseViewState, pauseViewState } from '../../shared/utils/subscription-pause.util';

/**
 * CWS-1536: Payment method is offered on this membership: the card on file of a Way membership,
 * or, with `label` null, the vendor an imported one's payments go through.
 */
interface PaymentMethodOffer {
  subscriptionId: string;
  label: string | null;
  billedBy: string | null;
}

@Component({
  selector: 'app-multi-eligible-selection-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WashHistoryPanelComponent,
    RewashReasonModalComponent,
    PlanChangePanelComponent,
    ConfirmActionModalComponent,
    ManageSubscriptionMenuComponent,
    PauseSubscriptionModalComponent,
    PaymentMethodModalComponent,
    FamilyMembersModalComponent,
    StartFamilyModalComponent,
    SubscriptionPauseNoticeComponent,
    SubscriptionResultModalComponent,
  ],
  templateUrl: './multi-eligible-selection-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./multi-eligible-selection-modal.component.scss'],
})
export class MultiEligibleSelectionModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  mode: MultiEligibleModalMode = 'lpr';
  step: MultiEligibleWizardStep = 'package';
  notification: NotificationData | null = null;
  selectionKind: MultiEligibleSelectionKind = 'packages';
  plateDisplay = '—';
  plateState = '';
  customerName = '';
  customerPhone = '';
  customerFirstName = '';
  customerLastName = '';
  customerEmail = '';
  phoneInput = '';
  cards: MultiEligibleOptionCardVm[] = [];
  vehicleCards: MultiEligibleVehicleCardVm[] = [];
  selectedCardId: string | null = null;
  selectedVehicleId: string | null = null;
  selectedSlotKey: string | null = null;
  isConfirming = false;
  isLoading = false;
  confirmError = '';

  prepaidStatus: PrepaidPurchaseStatus | null = null;
  prepaidStatusLoading = false;
  prepaidStatusError = '';
  washTab: PrepaidRedemptionSlotTab = 'active';
  activeWashSlots: PrepaidRedemptionSlot[] = [];
  redeemedWashSlots: PrepaidRedemptionSlot[] = [];
  redeemedSlotsLoading = false;
  redeemedSlotsError = '';

  /** Shared wash-history panel (same as sales / vendor Active subscription). */
  isLoadingHistory = false;
  historyBanner: OrderRequestTodayBannerState | null = null;
  historyExpanded = false;
  rewashEligibility: RewashEligibilityState | null = null;
  membershipRewashEligibility: MembershipRewashEligibilityState | null = null;
  /** CWS-1882: rewash candidates for prepaid visits, which have no order of their own. */
  prepaidRewashEligibility: PrepaidRewashEligibilityState | null = null;
  rewashReasonModalOpen = false;
  rewashTarget: OrderRequestRewashTarget | null = null;

  private selectedOption: MultiEligiblePurchaseOption | null = null;

  /** CWS-1426: Manage Subscription → Update for the selected Way membership. */
  private readonly planChangeSettings = inject(PlanChangeSettingsService);
  /** CWS-1469: pause and Activate now are for Attendant+ and Admin, as the API enforces. */
  private readonly roleService = inject(RoleService);
  showPlanChange = false;
  planChangeNotice: { title: string; body: string } | null = null;
  private planChangeAvailable = false;
  private planChangeAvailabilityKey: string | null = null;

  /** CWS-1581: Manage Subscription → Start Family for the selected Way membership. */
  /** The add-members step that follows a started family. */
  readonly membersFlow = new FamilyMembersFlowController(inject(FamilyMembersService), {
    onChanged: () => this.ordersService.refreshOrders(),
  });
  readonly startFamilyFlow = new StartFamilyFlowController(inject(FamilyStartService), {
    members: this.membersFlow,
    onDone: (_result, subject) => this.onStartFamilyDone(subject.subscriptionId),
  });
  private startFamilyOffered = false;
  private startFamilyLookupKey: string | null = null;

  /** CWS-1536: Manage Subscription → Update Payment method for the selected membership. */
  private readonly paymentMethods = inject(SubscriptionPaymentMethodService);
  paymentMethodSubject: PaymentMethodSubject | null = null;
  /**
   * The membership the card lookup last ran for, and what it found when Payment method is offered:
   * the card on file of a Way membership, or the vendor of an imported one (`label` null).
   */
  private cardLookupKey: string | null = null;
  private cardOnFile: PaymentMethodOffer | null = null;
  /** The in-flight card lookup; cancelled when a newer one starts or the modal resets. */
  private cardLookup: Subscription | null = null;

  private phoneCustomerId = '';
  private activePrepaidStatusRequestId: string | null = null;
  private activeRedeemedSlotsRequestId: string | null = null;
  private readonly prepaidStatusCache = new Map<string, PrepaidPurchaseStatus>();
  private readonly destroy$ = new Subject<void>();
  /** CWS-1547: pause / Activate now for the selected Way membership. */
  readonly pauseFlow: PauseFlowController;
  private historyContextKey: string | null = null;
  private historyBuildInFlightKey: string | null = null;

  constructor(
    private readonly modalState: MultiEligibleSelectionModalStateService,
    private readonly confirmApi: MultiEligibleConfirmApiService,
    private readonly modalStateService: ModalStateService,
    private readonly wayPurchaseModalState: WayPurchaseModalStateService,
    private readonly wayPurchaseApi: WayPurchaseApiService,
    private readonly vehicleService: VehicleService,
    private readonly prepaidWashBookService: PrepaidWashBookService,
    private readonly locationService: LocationService,
    private readonly employeeService: EmployeeService,
    private readonly apiService: ApiService,
    private readonly ordersService: OrdersService,
    private readonly historyService: NotificationOrderRequestHistoryService,
    private readonly toastService: ToastService,
    private readonly laneAttentionService: LaneAttentionService,
    private readonly customerSubscriptions: CustomerSubscriptionService,
    pauseActions: SubscriptionPauseActionsService,
    // Angular 22 zoneless CD: SSE/HTTP state updates must markForCheck or the overlay never paints.
    private readonly cdr: ChangeDetectorRef
  ) {
    this.pauseFlow = pauseActions.createFlow({
      resultHeader: outcome =>
        outcome.kind === 'paused' || outcome.kind === 'scheduled'
          ? 'Pause Subscription'
          : 'Active Subscription',
      onDone: outcome => this.onPauseDone(outcome),
      onStale: () => this.refreshSelectedPause(),
    });
  }

  ngOnInit(): void {
    this.modalState.modalState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.applyState(state);
      // Angular 22 zoneless CD: SSE/HTTP opens never paint without an explicit CD pass
      // (same pattern as notification-modal).
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.pauseFlow.destroy();
    this.startFamilyFlow.destroy();
    this.membersFlow.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get modalTitle(): string {
    if (this.showPlanChange) {
      return 'Update membership';
    }
    if (this.rewashChoiceRequired && this.step === 'package') {
      return 'Active Subscription';
    }
    return resolveMultiEligibleModalTitle(this.selectionKind, this.step);
  }

  get chooserLabel(): string {
    return resolveMultiEligibleChooserLabel(this.selectionKind, this.step, this.cards);
  }

  /** Membership / package / single-wash list under Choose Vehicle (Figma: same screen). */
  get packageChooserLabel(): string {
    return resolveMultiEligibleChooserLabel(this.selectionKind, 'package', this.cards);
  }

  /** Cards grouped as Single wash / Monthly membership / Prepaid wash. */
  get cardCategories(): MultiEligibleOptionCategoryVm[] {
    return groupMultiEligibleOptionCards(this.cards);
  }

  /** Show category subheadings when more than one product type is present. */
  get showPackageCategories(): boolean {
    return this.cardCategories.length > 1;
  }

  get showVehicleChooser(): boolean {
    return this.step === 'vehicle';
  }

  /** Phone multi-plate: show packages under vehicles; LPR / single-plate uses package step alone. */
  get showPackageChooser(): boolean {
    return this.step === 'package' || (this.step === 'vehicle' && this.cards.length > 0);
  }

  /** Customer strip shows the plate number without its state prefix. */
  get plateNumberDisplay(): string {
    return stripMultiEligiblePlateState(this.plateDisplay, this.plateState);
  }

  /** Figma variant 2 hides plate in the customer strip while choosing a vehicle. */
  get showPlateInCustomerStrip(): boolean {
    return this.step !== 'vehicle' && this.step !== 'phone';
  }

  get showCustomerStrip(): boolean {
    return this.step !== 'phone';
  }

  /** Wash history once customer + vehicle are known (hidden on phone entry). */
  get canShowWashHistory(): boolean {
    if (!this.isOpen || this.step === 'phone') {
      return false;
    }
    const { customerId, vehicleId } = this.resolveHistoryIds();
    return Boolean(customerId && vehicleId);
  }

  /** LPR forced attendant when a wash is rewash-eligible (may have 0 redeem cards). */
  get rewashChoiceRequired(): boolean {
    const data = (this.notification?.data ?? {}) as Record<string, any>;
    return data['rewash_choice_required'] === true;
  }

  get rewashLaneId(): string | null {
    const fromNotification = String(
      (this.notification?.data as Record<string, any> | undefined)?.['lane_id'] ??
        (this.notification?.data as Record<string, any> | undefined)?.['laneId'] ??
        ''
    ).trim();
    return fromNotification || getUserDataLaneId() || null;
  }

  /**
   * Follows the package chooser rather than the step: on the phone path the packages render
   * under the vehicles on the 'vehicle' step, and pinning this to 'package' meant picking the
   * wash book there showed the card but never its contents -- no item breakdown, no remaining
   * count, no Select Wash to Redeem. The status is already loaded by then; only the panel was
   * missing.
   */
  get showPrepaidExpand(): boolean {
    return this.showPackageChooser && optionIsPrepaidPurchase(this.selectedOption);
  }

  get prepaidWashbookTitle(): string {
    return buildPrepaidWashbookTitle(this.prepaidStatus);
  }

  get prepaidSummaryCards(): PrepaidPurchaseStatusSummaryCard[] {
    return getPrepaidPurchaseStatusSummaryCards(this.prepaidStatus);
  }

  get visibleWashSlots(): PrepaidRedemptionSlot[] {
    return this.washTab === 'active' ? this.activeWashSlots : this.redeemedWashSlots;
  }

  get showFullyRedeemedMessage(): boolean {
    return this.washTab === 'active' && isPrepaidPurchaseFullyRedeemed(this.prepaidStatus);
  }

  get primaryButtonLabel(): string {
    if (this.isConfirming) {
      return 'Redeeming…';
    }
    if (this.step === 'phone') {
      return this.isLoading ? 'Searching…' : 'Continue';
    }
    // Vehicle + membership on one screen (Figma) → Redeem wash, not Continue.
    if (this.step === 'vehicle' && this.cards.length === 0) {
      return 'Continue';
    }
    // CWS-1915: a prepaid book continues to Redeem Prepaid Purchase, where the wash and the
    // person redeeming are captured; the redeem itself happens there.
    if (optionIsPrepaidPurchase(this.selectedOption)) {
      return 'Continue to redeem';
    }
    return 'Redeem wash';
  }

  get canSubmitPrimary(): boolean {
    if (this.isConfirming || this.isLoading || this.prepaidStatusLoading) {
      return false;
    }
    if (this.step === 'phone') {
      return this.phoneInput.replace(/\D/g, '').length >= 7;
    }
    if (this.step === 'vehicle' && this.cards.length === 0) {
      return Boolean(this.selectedVehicleId);
    }
    if (this.step === 'package' || (this.step === 'vehicle' && this.cards.length > 0)) {
      if (!this.selectedCardId || this.selectedOption?.redeemable === false) {
        return false;
      }
      if (optionIsPrepaidPurchase(this.selectedOption)) {
        // CWS-1915: the wash is picked on Redeem Prepaid Purchase; here only the book is chosen.
        return !this.prepaidStatusError;
      }
      return true;
    }
    return Boolean(this.selectedSlotKey);
  }

  get showBackButton(): boolean {
    if (this.isConfirming || this.isLoading) {
      return false;
    }
    // Combined Choose Vehicle + Membership: no Back to phone search.
    if (this.mode === 'phone') {
      return this.step === 'package' && this.vehicleCards.length > 1;
    }
    return false;
  }

  onClose(): void {
    if (this.isConfirming) {
      return;
    }
    this.modalState.hideModal();
  }

  // ------------------------------------------------------------ plan change

  /** The selected membership's subscription id, when it is a membership at all. */
  get planChangeSubscriptionId(): string | null {
    const option = this.selectedOption;
    if (!option || option.redeem_event !== 'active-subscription') return null;
    return String(option.subscription_id ?? '').trim() || null;
  }

  get planChangeLocationId(): string | null {
    const fromShop = this.resolvePhoneRedeemLocationId();
    if (fromShop) return fromShop;
    const fromNotification = (this.notification?.data as Record<string, unknown> | undefined)?.[
      'location_id'
    ];
    return String(fromNotification ?? '').trim() || null;
  }

  /**
   * Manage Subscription is offered for the selected Way membership when the
   * tenant switched plan changes on at this location. Client gating only; the
   * API re-checks. No role restriction (Product, 30 Sep 2026).
   */
  get canChangePlan(): boolean {
    if (this.isConfirming || this.isLoading || this.showPlanChange) return false;
    if (!(this.step === 'package' || (this.step === 'vehicle' && this.cards.length > 0))) {
      return false;
    }
    const option = this.selectedOption;
    const subscriptionId = this.planChangeSubscriptionId;
    if (!subscriptionId) return false;
    const source = String(option?.subscription_source ?? 'way').toLowerCase();
    if (source !== 'way') return false;
    this.ensurePlanChangeAvailability(subscriptionId);
    return this.planChangeAvailable;
  }

  private ensurePlanChangeAvailability(subscriptionId: string): void {
    const locationId = this.planChangeLocationId;
    const key = locationId ? `${subscriptionId}|${locationId}` : null;
    if (key === this.planChangeAvailabilityKey) return;
    this.planChangeAvailabilityKey = key;
    this.planChangeAvailable = false;
    if (!key || !locationId) return;
    this.planChangeSettings
      .getAvailability(locationId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe(res => {
        if (this.planChangeAvailabilityKey !== key) return;
        this.planChangeAvailable = res?.data?.enabled === true;
        this.cdr.markForCheck();
      });
  }

  onManageUpdate(): void {
    this.planChangeNotice = null;
    this.confirmError = '';
    this.showPlanChange = true;
  }

  onPlanChangeBack(): void {
    this.showPlanChange = false;
  }

  /** Reflect an applied or scheduled change on the selected card and reset availability. */
  onPlanChangeApplied(result: PlanChangeResult): void {
    const membership = result.membership;
    const notice = buildPlanChangeNotice(result);
    this.planChangeNotice = notice ? { title: notice.title, body: notice.body } : null;
    if (membership && result.change?.timing === 'immediate') {
      const card = this.cards.find(c => c.id === this.selectedCardId);
      if (card) {
        card.packageName = membership.new_plan.name;
        card.option = {
          ...card.option,
          package_id: membership.new_plan.package_id,
          package_name: membership.new_plan.name,
          washes_included: membership.unlimited ? null : (membership.washes_this_cycle ?? null),
        };
        this.selectedOption = card.option;
      }
    }
    this.planChangeAvailabilityKey = null;
    this.ordersService.refreshOrders();
    this.cdr.markForCheck();
  }

  // ------------------------------------------------------------------ pause

  /** The selected Way membership's pause view; null when it has no pause block (feature off). */
  get pauseView(): PauseViewState | null {
    const option = this.selectedOption;
    if (!option?.pause || !this.planChangeSubscriptionId || !this.onMembershipStep) {
      return null;
    }
    if (String(option.subscription_source ?? 'way').toLowerCase() !== 'way') {
      return null;
    }
    return pauseViewState(
      { is_live: true, pause: option.pause },
      {
        updateAvailable: this.canChangePlan,
        canManage: this.roleService.canManageCustomers(),
        paymentMethodAvailable: this.canChangePaymentMethod,
        startFamilyAvailable: this.canStartFamily,
      }
    );
  }

  private get onMembershipStep(): boolean {
    return this.step === 'package' || (this.step === 'vehicle' && this.cards.length > 0);
  }

  private get actionsIdle(): boolean {
    return !(this.isConfirming || this.isLoading || this.showPlanChange || this.pauseFlow.busy);
  }

  /**
   * Manage Subscription: pause rules when the membership has a pause block, else plan change and
   * Payment method.
   */
  get showManageMenu(): boolean {
    const view = this.pauseView;
    if (view) return this.actionsIdle && view.manage.visible;
    return (
      this.canChangePlan ||
      (this.actionsIdle && (this.canChangePaymentMethod || this.canStartFamily))
    );
  }

  // ------------------------------------------------------------ start family (CWS-1581)

  /**
   * Start Family is offered for the selected Way membership on a family-enabled package that is
   * not in a family yet, where Family Plan is on. The pop-up refuses what the card cannot know.
   */
  get canStartFamily(): boolean {
    if (!this.actionsIdle || !this.onMembershipStep || !this.planChangeSubscriptionId) {
      return false;
    }
    this.ensureStartFamilyAvailability();
    return this.startFamilyOffered && this.selectedOption?.is_family !== true;
  }

  get showStartFamilyItem(): boolean {
    const view = this.pauseView;
    return view ? view.manage.startFamilyVisible : this.canStartFamily;
  }

  /** One lookup per membership: the subscriptions list carries the server's `can_start_family`. */
  private ensureStartFamilyAvailability(): void {
    const subscriptionId = this.planChangeSubscriptionId;
    if (subscriptionId === this.startFamilyLookupKey) return;
    this.startFamilyLookupKey = subscriptionId;
    this.startFamilyOffered = false;
    if (!subscriptionId) return;
    this.fetchSubscription()
      .pipe(
        map(sub => sub?.can_start_family === true),
        catchError(() => of(false)),
        takeUntil(this.destroy$)
      )
      .subscribe(offered => {
        if (this.startFamilyLookupKey !== subscriptionId) return;
        this.startFamilyOffered = offered;
        this.cdr.markForCheck();
      });
  }

  onManageStartFamily(): void {
    const subject = this.pauseSubject();
    if (!subject || !this.canStartFamily) return;
    this.startFamilyFlow.open({
      subscriptionId: subject.subscriptionId,
      plate: subject.plate,
      packageName: subject.planName,
      lprEntryId: this.lprEntryIdFromNotification(this.notification) || null,
    });
  }

  /** The membership is the primary of a new family: tag its card and refresh the lists behind. */
  private onStartFamilyDone(subscriptionId: string): void {
    // The membership the family was started on, not whichever card is selected by now: a state
    // refresh while the call was in flight resets the selection.
    const card = this.cards.find(c => c.option.subscription_id === subscriptionId);
    if (card) {
      card.option = { ...card.option, is_family: true, is_primary: true };
      card.isFamily = true;
      card.isPrimary = true;
      if (card.id === this.selectedCardId) this.selectedOption = card.option;
    }
    this.ordersService.refreshOrders();
    this.cdr.markForCheck();
  }

  /** CWS-1536: Update Payment method in the Manage Subscription menu. */
  get showPaymentMethod(): boolean {
    const view = this.pauseView;
    return view ? view.manage.paymentMethodVisible : this.canChangePaymentMethod;
  }

  get showActivate(): boolean {
    return this.actionsIdle && this.pauseView?.showActivate === true;
  }

  get flowError(): string {
    return pauseRefusalCopy(this.pauseFlow.errorCode);
  }

  get activateMessage(): string {
    return activateConfirmCopy(this.pauseFlow.activateTarget?.bankedDays);
  }

  onManagePause(): void {
    const base = this.pauseSubject();
    if (!base) return;
    // The lane payload has no period end; read it so the modal can project "Next renewal date".
    this.fetchSubscription()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sub => {
        // Another membership was selected while the date was loading.
        if (this.planChangeSubscriptionId !== base.subscriptionId) return;
        this.pauseFlow.openPause({ ...base, periodEnd: sub?.current_period_end ?? null });
        this.cdr.markForCheck();
      });
  }

  onManageCancelScheduledPause(): void {
    const subject = this.pauseSubject();
    if (subject) this.pauseFlow.askCancelScheduled(subject);
  }

  onActivateNow(): void {
    const subject = this.pauseSubject();
    if (subject) {
      this.pauseFlow.askActivate({
        ...subject,
        bankedDays: this.selectedOption?.pause?.banked_days,
      });
    }
  }

  private pauseSubject(): PauseSubject | null {
    const subscriptionId = this.planChangeSubscriptionId;
    const option = this.selectedOption;
    if (!subscriptionId || !option) return null;
    const vehicle = this.vehicleCards.find(v => v.id === this.selectedVehicleId);
    return {
      subscriptionId,
      plate: vehicle?.licensePlate || this.plateDisplay,
      planName: option.package_name || 'Membership',
      periodEnd: null,
      timeZone: option.pause?.time_zone ?? null,
    };
  }

  private fetchSubscription(): Observable<CustomerSubscription | null> {
    const customerId = this.selectedOption?.customer_id;
    const subscriptionId = this.planChangeSubscriptionId;
    if (!customerId || !subscriptionId) return of(null);
    return this.customerSubscriptions.getSubscription(customerId, subscriptionId);
  }

  // ------------------------------------------------------------ payment method (CWS-1536)

  /**
   * Update Payment method is offered for the selected membership to Attendant+ and Admin: a Way
   * one once svc-order says its card can be changed (switch on, membership eligible), an imported
   * one, as its vendor notice, when its company switched that on. The lookup that answers this
   * runs once per membership per opening and also fills "Card on file".
   */
  get canChangePaymentMethod(): boolean {
    if (!this.onMembershipStep || !this.roleService.canManageCustomers()) return false;
    const subscriptionId = this.planChangeSubscriptionId;
    if (!subscriptionId) return false;
    this.ensureCardOnFile(subscriptionId);
    return this.cardOnFile?.subscriptionId === subscriptionId;
  }

  /** "Visa •••• 4242" (or "—") for the selected membership's card; null leaves the line out. */
  get selectedCardOnFile(): string | null {
    return this.canChangePaymentMethod ? (this.cardOnFile?.label ?? null) : null;
  }

  private ensureCardOnFile(subscriptionId: string): void {
    if (subscriptionId === this.cardLookupKey) return;
    this.cardLookup?.unsubscribe();
    this.cardLookupKey = subscriptionId;
    this.cardOnFile = null;
    // No source is Way, as for plan change.
    const billedBy = importedBilledBy(this.selectedOption?.subscription_source);
    // Only once Payment method is known to be on: switched off, a lane arrival costs no request.
    // An import offers it only when a card can be saved for it: a gate order at a shop with Stripe.
    const answer$: Observable<PaymentMethodOffer> = billedBy
      ? this.paymentMethods.isImportedEnabled().pipe(
          filter(Boolean),
          switchMap(() => this.paymentMethods.canSaveImportedCard(subscriptionId)),
          filter(Boolean),
          map(() => ({ subscriptionId, label: null, billedBy }))
        )
      : this.paymentMethods.isEnabled().pipe(
          filter(Boolean),
          switchMap(() => this.paymentMethods.renewalCard(subscriptionId)),
          map(card => ({
            subscriptionId,
            label: card ? cardWithExpiryLabel(card) : '—',
            billedBy: null,
          }))
        );
    this.cardLookup = answer$.pipe(takeUntil(this.destroy$)).subscribe(answer => {
      this.cardOnFile = answer;
      this.cdr.markForCheck();
    });
  }

  /** Open the shared Payment method pop-up; the lane payload has no renewal date or price. */
  onManagePaymentMethod(): void {
    const base = this.pauseSubject();
    if (!base || !this.showPaymentMethod) return;
    this.fetchSubscription()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sub => {
        // Another membership was selected while it was loading.
        if (this.planChangeSubscriptionId !== base.subscriptionId) return;
        this.paymentMethodSubject = {
          subscriptionId: base.subscriptionId,
          planName: base.planName,
          plate: base.plate,
          nextRenewal: sub?.next_billing_date ?? null,
          price: sub?.price ?? null,
          billedBy: this.cardOnFile?.billedBy ?? null,
        };
        this.cdr.markForCheck();
      });
  }

  /** The renewal card changed: show it on the selected card at once and confirm with a toast. */
  onPaymentMethodUpdated(card: SavedCard): void {
    const subject = this.paymentMethodSubject;
    this.paymentMethodSubject = null;
    if (subject) {
      this.cardOnFile = {
        subscriptionId: subject.subscriptionId,
        label: cardWithExpiryLabel(card),
        billedBy: null,
      };
    }
    const toast = renewalCardUpdatedToast(card, subject);
    this.toastService.showSuccess(toast.title, toast.detail);
    this.cdr.markForCheck();
  }

  closePaymentMethod(): void {
    this.paymentMethodSubject = null;
  }

  private onPauseDone(outcome: PauseOutcome): void {
    if (outcome.pause) {
      this.applyPauseToSelected(outcome.pause);
    } else {
      this.refreshSelectedPause();
    }
    this.ordersService.refreshOrders();
  }

  /** A stale pause (another device changed it): re-read the block and repaint the card. */
  private refreshSelectedPause(): void {
    this.fetchSubscription()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sub => {
        if (sub?.pause) this.applyPauseToSelected(sub.pause);
      });
  }

  /** Put a fresh pause block on the selected membership, its card, and its vehicle's list. */
  private applyPauseToSelected(pause: SubscriptionPause): void {
    const subscriptionId = this.planChangeSubscriptionId;
    if (!subscriptionId) return;
    const patch = (option: MultiEligiblePurchaseOption): MultiEligiblePurchaseOption =>
      option.subscription_id === subscriptionId
        ? {
            ...option,
            pause,
            redeemable: pause.state === 'paused' ? false : undefined,
            reactivated:
              pause.state !== 'paused' && (option.reactivated || option.pause?.state === 'paused'),
          }
        : option;
    for (const vehicle of this.vehicleCards) {
      vehicle.memberships = vehicle.memberships.map(patch);
    }
    const rebuilt = buildMultiEligibleOptionCards(this.cards.map(card => patch(card.option)));
    const byId = new Map(rebuilt.map(card => [card.id, card]));
    this.cards = this.cards.map(card => byId.get(card.id) ?? card);
    this.selectedOption =
      this.cards.find(c => c.id === this.selectedCardId)?.option ?? this.selectedOption;
    this.cdr.markForCheck();
  }

  onBack(): void {
    if (!this.showBackButton) {
      return;
    }
    this.confirmError = '';
    if (this.step === 'package' && this.mode === 'phone') {
      this.step = 'vehicle';
      this.cards = [];
      this.selectedCardId = null;
      this.selectedOption = null;
      this.clearPrepaidExpand();
      return;
    }
    if (this.step === 'vehicle') {
      this.step = 'phone';
      this.vehicleCards = [];
      this.selectedVehicleId = null;
      this.clearWashHistoryState();
    }
  }

  onSelectCard(card: MultiEligibleOptionCardVm): void {
    if (this.isConfirming) {
      return;
    }
    this.selectedCardId = card.id;
    this.selectedOption = card.option;
    this.confirmError = '';
    this.applyPrepaidSelection(card.option);
    this.ensureWashHistory();
    this.syncView();
  }

  onSelectVehicle(card: MultiEligibleVehicleCardVm): void {
    if (this.isConfirming) {
      return;
    }
    this.selectedVehicleId = card.id;
    this.confirmError = '';
    this.syncMembershipsForSelectedVehicle();
    this.ensureWashHistory();
    this.syncView();
  }

  onSelectWashSlot(slot: PrepaidRedemptionSlot): void {
    if (this.isConfirming || slot.tab !== 'active') {
      return;
    }
    this.selectedSlotKey = slot.slotKey;
    this.confirmError = '';
    this.syncView();
  }

  setWashTab(tab: PrepaidRedemptionSlotTab): void {
    this.washTab = tab;
    if (tab === 'redeemed') {
      this.ensureRedeemedSlotsLoaded();
    }
    this.syncView();
  }

  formatWashSlotSubline(slot: PrepaidRedemptionSlot): string {
    return formatPrepaidRedemptionSlotSubline(slot);
  }

  onRetryPrepaidStatus(): void {
    if (!this.selectedOption) {
      return;
    }
    const prepaidId = String(this.selectedOption.prepaid_purchase_id ?? '').trim();
    if (prepaidId) {
      this.prepaidStatusCache.delete(prepaidId);
    }
    this.loadPrepaidStatusForOption(this.selectedOption);
  }

  onRetryRedeemedSlots(): void {
    this.redeemedSlotsError = '';
    this.redeemedWashSlots = [];
    this.ensureRedeemedSlotsLoaded();
  }

  onPrimaryAction(): void {
    if (!this.canSubmitPrimary) {
      this.syncView();
      return;
    }
    if (this.step === 'phone') {
      this.lookupPhone();
      return;
    }
    if (this.step === 'vehicle' && this.cards.length === 0) {
      this.advanceFromVehicle();
      return;
    }
    this.redeemSelected();
  }

  /** Family plan tag text (CWS-1582 §8). */
  readonly familyTagLabel = FAMILY_TAG_LABEL;
  readonly familyPrimaryLabel = FAMILY_PRIMARY_TAG_LABEL;

  trackByCardId(_index: number, card: MultiEligibleOptionCardVm): string {
    return card.id;
  }

  trackByCategoryKey(_index: number, category: MultiEligibleOptionCategoryVm): string {
    return category.key;
  }

  trackByVehicleId(_index: number, card: MultiEligibleVehicleCardVm): string {
    return card.id;
  }

  trackByWashSlotKey(_index: number, slot: PrepaidRedemptionSlot): string {
    return slot.slotKey;
  }

  trackBySummaryCardId(_index: number, card: PrepaidPurchaseStatusSummaryCard): string {
    return card.purchaseItemId;
  }

  private applyState(state: MultiEligibleSelectionModalState): void {
    const wasOpen = this.isOpen;
    const previousNotification = this.notification;
    const preservePhoneWizard =
      this.isOpen &&
      state.isOpen &&
      this.mode === 'phone' &&
      state.mode === 'phone' &&
      this.step !== 'phone' &&
      Boolean(state.phoneLookupPayload) &&
      String(state.phoneLookupPayload?.customer?.id ?? '') === this.phoneCustomerId;

    this.isOpen = state.isOpen;
    this.mode = state.mode;
    this.notification = state.notification;
    this.confirmError = '';
    this.isConfirming = false;
    this.isLoading = false;
    this.showPlanChange = false;
    this.planChangeNotice = null;
    this.planChangeAvailabilityKey = null;
    this.planChangeAvailable = false;
    // CWS-1536: each opening reads the card again, so a card changed elsewhere shows.
    this.cardLookup?.unsubscribe();
    this.cardLookupKey = null;
    this.cardOnFile = null;
    this.paymentMethodSubject = null;

    // CWS-1246: clear the stacked lane-attention toast when the chooser closes
    // (confirm, rewash, or X) so "Active Subscription" does not linger in the background.
    // Also dismiss when replace keeps isOpen true but swaps to a different LPR entry —
    // otherwise the previous entry's toast stays stacked behind the new chooser.
    if (wasOpen) {
      const previousEntryId = this.lprEntryIdFromNotification(previousNotification);
      const nextEntryId = this.lprEntryIdFromNotification(state.notification);
      if (!state.isOpen || previousEntryId !== nextEntryId) {
        this.dismissLaneAttentionForNotification(previousNotification);
      }
    }

    if (preservePhoneWizard) {
      return;
    }

    this.clearPrepaidExpand();
    this.selectedOption = null;
    this.phoneCustomerId = '';
    this.customerName = '';
    this.customerPhone = '';
    this.customerFirstName = '';
    this.customerLastName = '';
    this.customerEmail = '';

    if (!state.isOpen) {
      this.cards = [];
      this.vehicleCards = [];
      this.selectedCardId = null;
      this.selectedVehicleId = null;
      this.phoneInput = '';
      this.step = 'package';
      this.prepaidStatusCache.clear();
      this.clearWashHistoryState();
      return;
    }

    if (state.mode === 'phone') {
      this.phoneInput = state.phoneSeed ?? '';
      this.plateDisplay = '—';
      this.plateState = '';
      this.cards = [];
      this.vehicleCards = [];
      this.selectedCardId = null;
      this.selectedVehicleId = null;
      this.confirmError = '';
      // Create-order already fetched memberships (+ optional Way) — skip phone search.
      if (state.phoneLookupPayload) {
        if (state.phoneWayPurchases != null) {
          this.applyPhoneLookupPayload(state.phoneLookupPayload, state.phoneWayPurchases);
        } else {
          this.hydratePhoneLookupWithWay(state.phoneLookupPayload);
        }
        return;
      }
      this.step = 'phone';
      if (this.phoneInput.replace(/\D/g, '').length >= 7) {
        this.lookupPhone();
      }
      this.clearWashHistoryState();
      return;
    }

    this.step = 'package';
    const data = (state.notification?.data ?? {}) as Record<string, any>;
    const options = readMultiEligiblePurchaseOptions(data);
    this.selectionKind = resolveMultiEligibleSelectionKind(data, options);
    this.plateDisplay = resolveMultiEligiblePlateDisplay(data);
    this.plateState = resolveMultiEligiblePlateState(data);
    this.customerName = resolveMultiEligibleCustomerName(data);
    this.customerPhone = resolveMultiEligibleCustomerPhone(data);
    this.customerFirstName = String(data['customer_first_name'] ?? '').trim();
    this.customerLastName = String(data['customer_last_name'] ?? '').trim();
    this.customerEmail = String(data['customer_email'] ?? '').trim();
    this.cards = buildMultiEligibleOptionCards(options);
    this.selectedCardId = this.cards[0]?.id ?? null;
    this.selectedOption = this.cards[0]?.option ?? null;
    // Seed rewash eligibility from LPR payload so CTA is available before history loads.
    this.rewashEligibility = parseRewashEligibilityFromHistoryResponse({
      rewash_eligibility: data['rewash_eligibility'],
    });
    this.membershipRewashEligibility = parseMembershipRewashEligibilityFromHistoryResponse({
      membership_rewash_eligibility: data['membership_rewash_eligibility'],
    });
    this.prepaidRewashEligibility = parsePrepaidRewashEligibilityFromHistoryResponse({
      prepaid_rewash_eligibility: data['prepaid_rewash_eligibility'],
    });
    if (this.selectedOption) {
      this.applyPrepaidSelection(this.selectedOption);
    }
    // LPR may follow a redeem on the same plate in this session — drop stale history
    // cache so the latest subscription wash appears (do not clear LPR-seeded eligibility).
    const { vehicleId } = this.resolveHistoryIds();
    if (vehicleId) {
      this.historyService.invalidateForVehicle(vehicleId);
    }
    this.historyBanner = null;
    this.historyContextKey = null;
    this.historyBuildInFlightKey = null;
    this.ensureWashHistory();
  }

  private lookupPhone(): void {
    const phone = this.phoneInput.trim();
    if (!phone) {
      return;
    }
    this.isLoading = true;
    this.confirmError = '';
    const locationId = this.resolvePhoneRedeemLocationId();
    const memberships$ = this.vehicleService.getVehiclesWithMembershipsByPhone(phone);
    const way$ = this.resolveWayPurchaseLookup$(locationId, phone);

    forkJoin({ memberships: memberships$, way: way$ })
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: ({ memberships, way }) => {
          this.applyPhoneLookupPayload(memberships?.data, way?.data?.purchases ?? []);
          this.syncView();
        },
        error: (error: unknown) => {
          this.confirmError = extractErrorMessage(
            error,
            'Failed to look up memberships for this phone number.'
          );
          this.syncView();
        },
      });
  }

  /** Merge Way singles onto a preloaded phone memberships payload (create-order handoff). */
  private hydratePhoneLookupWithWay(payload: VehiclesWithMembershipsResponse): void {
    const phone = this.phoneInput.trim() || String(payload.customer?.phone ?? '').trim();
    const locationId = this.resolvePhoneRedeemLocationId();
    if (!locationId || !phone) {
      this.applyPhoneLookupPayload(payload, []);
      return;
    }
    this.isLoading = true;
    this.resolveWayPurchaseLookup$(locationId, phone)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: way => {
          this.applyPhoneLookupPayload(payload, way?.data?.purchases ?? []);
          this.syncView();
        },
      });
  }

  /**
   * Call Way purchase lookup only when the backend has advertised eligibility for this location.
   */
  private resolveWayPurchaseLookup$(
    locationId: string,
    phone: string
  ): Observable<{ data?: { purchases?: WayNormalizedPurchase[] } } | null> {
    const phoneDigits = String(phone ?? '').replace(/\D/g, '');
    if (!locationId || phoneDigits.length !== CUSTOMER_PHONE_MAX_DIGITS) {
      return of(null);
    }
    return this.resolveWayPurchaseLookupEnabled$(locationId).pipe(
      switchMap(enabled => {
        if (!enabled) {
          return of(null);
        }
        return this.wayPurchaseApi
          .lookupWayPurchases({ location_id: locationId, phone: phoneDigits })
          .pipe(catchError(() => of(null)));
      })
    );
  }

  private resolveWayPurchaseLookupEnabled$(locationId: string): Observable<boolean> {
    const fromCache = this.locationService.getLocationById(locationId);
    if (fromCache && typeof fromCache.way_purchase_lookup_enabled === 'boolean') {
      return of(isWayPurchaseLookupEnabled(fromCache));
    }
    const shop = this.locationService.getShopLocation();
    if (
      shop &&
      String(shop.id) === locationId &&
      typeof shop.way_purchase_lookup_enabled === 'boolean'
    ) {
      return of(isWayPurchaseLookupEnabled(shop));
    }
    return this.employeeService.getCurrentEmployeeProfile().pipe(
      take(1),
      map((response: { status?: boolean; data?: Record<string, unknown> }) => {
        if (!response?.status) {
          return false;
        }
        const profileLoc = String(response.data?.['location_id'] ?? '').trim();
        // Require an explicit matching location — an unscoped profile must not authorize Way.
        if (!profileLoc || profileLoc !== locationId) {
          return false;
        }
        return isWayPurchaseLookupEnabled(response.data);
      }),
      catchError(() => of(false))
    );
  }

  /** Zoneless CD: click/async handlers must refresh or radios and package lists stay stale. */
  private syncView(): void {
    this.cdr.detectChanges();
  }

  /** Hydrate Choose Vehicle (or package if one plate) from phone memberships + Way singles. */
  private applyPhoneLookupPayload(
    payload: VehiclesWithMembershipsResponse | null | undefined,
    wayPurchases: WayNormalizedPurchase[] = []
  ): void {
    if (!payload?.customer || !(payload.vehicles?.length > 0)) {
      this.confirmError = 'No vehicles with active memberships found for this phone number.';
      this.vehicleCards = [];
      this.step = 'phone';
      return;
    }
    this.phoneCustomerId = String(payload.customer.id ?? '');
    this.customerPhone = String(payload.customer.phone ?? this.phoneInput);
    this.customerFirstName = String(payload.customer.first_name ?? '').trim();
    this.customerLastName = String(payload.customer.last_name ?? '').trim();
    this.customerEmail = String(payload.customer.email ?? '').trim();
    this.customerName = [this.customerFirstName, this.customerLastName].filter(Boolean).join(' ');
    this.vehicleCards = this.mergeWaySinglesIntoVehicleCards(
      buildVehicleCardsFromPhoneResponse(payload),
      wayPurchases
    );
    if (this.vehicleCards.length === 0) {
      this.confirmError = 'No redeemable memberships or prepaid packages found.';
      this.step = 'phone';
      return;
    }
    this.selectedVehicleId = this.vehicleCards[0].id;
    this.isLoading = false;
    if (this.vehicleCards.length === 1) {
      this.advanceFromVehicle();
      return;
    }
    this.step = 'vehicle';
    this.syncMembershipsForSelectedVehicle();
    this.ensureWashHistory();
    this.syncView();
  }

  private mergeWaySinglesIntoVehicleCards(
    cards: MultiEligibleVehicleCardVm[],
    wayPurchases: WayNormalizedPurchase[]
  ): MultiEligibleVehicleCardVm[] {
    const wayOptions = (wayPurchases ?? [])
      .map(purchase => mapWayPurchaseToMultiEligibleOption(purchase as Record<string, unknown>))
      .filter((opt): opt is MultiEligiblePurchaseOption => Boolean(opt));
    if (!wayOptions.length) {
      return cards;
    }

    const normalizePlate = (value: string | null | undefined) =>
      String(value ?? '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');

    const usedWayIds = new Set<string>();
    const merged = cards.map(card => {
      const plate = normalizePlate(card.licensePlate);
      const extras = wayOptions.filter(opt => {
        const wayPassId = String(opt.way_pass_id ?? '');
        if (!wayPassId || usedWayIds.has(wayPassId)) {
          return false;
        }
        const purchasePlate = normalizePlate(
          String(
            (opt.purchase as Record<string, unknown> | null | undefined)?.['plate_number'] ?? ''
          )
        );
        // When Way returns a plate, attach only to that vehicle; otherwise share across plates.
        if (purchasePlate && plate && purchasePlate !== plate) {
          return false;
        }
        usedWayIds.add(wayPassId);
        return true;
      });
      if (!extras.length) {
        return card;
      }
      const memberships = [...card.memberships, ...extras];
      return {
        ...card,
        memberships,
        membershipCount: memberships.length,
      };
    });

    // Orphan Way singles (no matching plate / no local vehicles) → attach to first card.
    const orphans = wayOptions.filter(opt => !usedWayIds.has(String(opt.way_pass_id ?? '')));
    if (orphans.length && merged.length) {
      const [first, ...rest] = merged;
      const memberships = [...first.memberships, ...orphans];
      return [{ ...first, memberships, membershipCount: memberships.length }, ...rest];
    }
    return merged;
  }

  private advanceFromVehicle(): void {
    this.syncMembershipsForSelectedVehicle();
    this.step = 'package';
    this.ensureWashHistory();
    this.syncView();
  }

  /** Load Choose Membership / Choose Package cards for the selected plate (same screen as vehicles). */
  private syncMembershipsForSelectedVehicle(): void {
    const vehicle = this.vehicleCards.find(v => v.id === this.selectedVehicleId);
    if (!vehicle) {
      this.cards = [];
      this.selectedCardId = null;
      this.selectedOption = null;
      this.clearPrepaidExpand();
      return;
    }
    this.plateDisplay = vehicle.licensePlate;
    this.cards = buildMultiEligibleOptionCards(vehicle.memberships);
    this.selectionKind = resolveMultiEligibleSelectionKind(
      { selection_kind: undefined },
      vehicle.memberships
    );
    this.selectedCardId = this.cards[0]?.id ?? null;
    this.selectedOption = this.cards[0]?.option ?? null;
    if (this.selectedOption) {
      this.applyPrepaidSelection(this.selectedOption);
    } else {
      this.clearPrepaidExpand();
    }
  }

  private clearPrepaidExpand(): void {
    this.activePrepaidStatusRequestId = null;
    this.activeRedeemedSlotsRequestId = null;
    this.prepaidStatus = null;
    this.prepaidStatusLoading = false;
    this.prepaidStatusError = '';
    this.washTab = 'active';
    this.activeWashSlots = [];
    this.redeemedWashSlots = [];
    this.redeemedSlotsLoading = false;
    this.redeemedSlotsError = '';
    this.selectedSlotKey = null;
  }

  private applyPrepaidSelection(option: MultiEligiblePurchaseOption): void {
    if (!optionIsPrepaidPurchase(option)) {
      this.clearPrepaidExpand();
      return;
    }
    this.washTab = 'active';
    this.redeemedWashSlots = [];
    this.redeemedSlotsError = '';
    this.selectedSlotKey = null;
    this.loadPrepaidStatusForOption(option);
  }

  private isStillSelectedPrepaid(prepaidId: string): boolean {
    return (
      optionIsPrepaidPurchase(this.selectedOption) &&
      String(this.selectedOption?.prepaid_purchase_id ?? '').trim() === prepaidId
    );
  }

  private loadPrepaidStatusForOption(option: MultiEligiblePurchaseOption): void {
    const prepaidId = String(option.prepaid_purchase_id ?? '').trim();
    if (!prepaidId) {
      this.prepaidStatusError = 'Missing prepaid purchase id.';
      this.prepaidStatus = null;
      this.activeWashSlots = [];
      return;
    }

    this.activePrepaidStatusRequestId = prepaidId;

    const cached = this.prepaidStatusCache.get(prepaidId);
    if (cached) {
      if (this.isStillSelectedPrepaid(prepaidId)) {
        this.applyLoadedPrepaidStatus(option, cached);
      }
      return;
    }

    this.prepaidStatusLoading = true;
    this.prepaidStatusError = '';
    this.prepaidWashBookService
      .getPrepaidPurchaseStatus(prepaidId)
      .pipe(
        take(1),
        finalize(() => {
          if (this.activePrepaidStatusRequestId === prepaidId) {
            this.prepaidStatusLoading = false;
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: response => {
          if (!this.isStillSelectedPrepaid(prepaidId)) {
            return;
          }
          const status = response?.data ?? null;
          if (!status) {
            this.prepaidStatusError = 'Prepaid washbook details were not found.';
            this.prepaidStatus = null;
            this.activeWashSlots = [];
            return;
          }
          this.prepaidStatusCache.set(prepaidId, status);
          this.applyLoadedPrepaidStatus(option, status);
        },
        error: (error: unknown) => {
          if (!this.isStillSelectedPrepaid(prepaidId)) {
            return;
          }
          this.prepaidStatusError = extractErrorMessage(
            error,
            'Failed to load prepaid wash items. Please try again.'
          );
          this.prepaidStatus = null;
          this.activeWashSlots = [];
        },
      });
  }

  private applyLoadedPrepaidStatus(
    option: MultiEligiblePurchaseOption,
    status: PrepaidPurchaseStatus
  ): void {
    const prepaidId = String(option.prepaid_purchase_id ?? '').trim();
    if (!this.isStillSelectedPrepaid(prepaidId)) {
      return;
    }
    const enriched = attachPrepaidStatusItemsToOption(option, status);
    this.selectedOption = enriched;
    const cardIndex = this.cards.findIndex(c => c.id === this.selectedCardId);
    if (cardIndex >= 0) {
      this.cards = this.cards.map((c, i) =>
        i === cardIndex
          ? {
              ...c,
              option: enriched,
              packageName: enriched.package_name || c.packageName,
            }
          : c
      );
    }
    this.prepaidStatus = status;
    this.prepaidStatusError = '';
    this.activeWashSlots = expandPrepaidPurchaseStatusToRedemptionSlots(status);
    this.selectedSlotKey = this.activeWashSlots[0]?.slotKey ?? null;
    if (this.activeWashSlots.length === 0) {
      this.confirmError = isPrepaidPurchaseFullyRedeemed(status)
        ? 'Prepaid package has been fully redeemed.'
        : 'No redeemable wash items remain on this package.';
    }
    this.syncView();
  }

  private ensureRedeemedSlotsLoaded(): void {
    const prepaidId = String(this.selectedOption?.prepaid_purchase_id ?? '').trim();
    if (!prepaidId || this.redeemedSlotsLoading || this.redeemedWashSlots.length > 0) {
      return;
    }
    this.activeRedeemedSlotsRequestId = prepaidId;
    this.redeemedSlotsLoading = true;
    this.redeemedSlotsError = '';
    this.prepaidWashBookService
      .getPrepaidPurchaseRedemptions(prepaidId)
      .pipe(
        take(1),
        finalize(() => {
          if (this.activeRedeemedSlotsRequestId === prepaidId) {
            this.redeemedSlotsLoading = false;
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: response => {
          if (!this.isStillSelectedPrepaid(prepaidId)) {
            return;
          }
          this.redeemedWashSlots = mapPrepaidPurchaseRedemptionsToSlots(
            response?.redemptions ?? []
          );
        },
        error: (error: unknown) => {
          if (!this.isStillSelectedPrepaid(prepaidId)) {
            return;
          }
          this.redeemedSlotsError = extractErrorMessage(error, 'Failed to load redeemed washes.');
        },
      });
  }

  private redeemSelected(): void {
    const option = this.selectedOption;
    if (!option) {
      this.confirmError = 'Select a membership or package to redeem.';
      this.syncView();
      return;
    }
    const selectedItem = this.resolveSelectedWashItem(option);

    if (isWaySingleWashOption(option)) {
      this.confirmWaySingleWash(option);
      return;
    }

    // CWS-1915: a prepaid book is redeemed on Redeem Prepaid Purchase, which captures who
    // came in; the chooser only picks the book.
    if (option.redeem_event === 'redeem-prepaid-purchase') {
      this.handOffPrepaidRedeem(option);
      return;
    }

    if (this.mode === 'lpr') {
      this.confirmLprOption(option, selectedItem);
      return;
    }

    if (option.redeem_event === 'order-success') {
      this.confirmPhoneSingleWash(option);
      return;
    }

    this.confirmPhoneMembership(option);
  }

  private resolveSelectedWashItem(
    option: MultiEligiblePurchaseOption
  ): MultiEligiblePrepaidItem | null {
    if (option.redeem_event !== 'redeem-prepaid-purchase') {
      return null;
    }
    if (this.selectedSlotKey) {
      const slot = this.activeWashSlots.find(s => s.slotKey === this.selectedSlotKey);
      if (slot) {
        const match = listRedeemablePrepaidItems(option).find(
          item => item.purchase_item_id === slot.purchaseItemId
        );
        if (match) {
          return match;
        }
        return {
          purchase_item_id: slot.purchaseItemId,
          package_id: slot.packageId,
          wash_type: slot.type,
          package_name: slot.packageName,
          expires_at: slot.expiresAt,
        };
      }
    }
    const [first] = listRedeemablePrepaidItems(option);
    return first ?? null;
  }

  private confirmLprOption(
    option: MultiEligiblePurchaseOption,
    selectedItem: MultiEligiblePrepaidItem | null
  ): void {
    const payload = buildMultiEligibleConfirmRequest(
      (this.notification?.data ?? {}) as Record<string, any>,
      option,
      selectedItem
    );
    if (!payload) {
      this.confirmError =
        option.redeem_event === 'redeem-prepaid-purchase'
          ? 'No redeemable wash item found on this package.'
          : 'Missing package data. Cannot redeem this selection.';
      return;
    }

    this.confirmError = '';
    this.isConfirming = true;
    this.confirmApi
      .confirm(payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
        })
      )
      .subscribe({
        next: response => {
          this.modalState.hideModal();
          if (isMembershipRedeemEvent(payload.redeem_event)) {
            this.ordersService.refreshOrders({ resetPage: true });
            this.showMembershipRedeemSuccessToast(option);
            return;
          }
          const displayContext =
            payload.redeem_event === 'redeem-prepaid-purchase'
              ? this.buildPrepaidSuccessDisplayContext(option, selectedItem)
              : undefined;
          const successNotification = buildNotificationFromMultiEligibleConfirmResponse(
            response,
            payload.redeem_event,
            displayContext
          );
          if (successNotification) {
            openPusherNotificationModal(
              this.modalStateService,
              this.wayPurchaseModalState,
              successNotification,
              undefined,
              undefined,
              this.modalState
            );
          }
        },
        error: (error: unknown) => {
          this.confirmError = extractErrorMessage(
            error,
            'Failed to redeem selected package. Please try again.'
          );
          this.syncView();
        },
      });
  }

  /**
   * CWS-1915: close the chooser and open the create-order modal in prepaid redeem mode for
   * the chosen book, the same entry a QR scan or a plate match uses. That screen has the wash
   * picker and the plate / name / phone / email fields, and records the redeemer.
   */
  private handOffPrepaidRedeem(option: MultiEligiblePurchaseOption): void {
    const laneData = (this.notification?.data ?? null) as Record<string, any> | null;
    const notification = buildPrepaidRedeemHandoffNotification(option, {
      mode: this.mode === 'lpr' ? 'lpr' : 'phone',
      laneData,
      customerId: this.phoneCustomerId || null,
      vehicleId: this.selectedVehicleId || null,
      locationId: this.mode === 'lpr' ? null : this.resolvePhoneRedeemLocationId(),
      laneId: getUserDataLaneId() || null,
    });
    if (!notification) {
      this.confirmError = 'Missing customer or vehicle for prepaid redeem.';
      this.syncView();
      return;
    }
    this.confirmError = '';
    this.modalState.hideModal();
    this.modalStateService.clearNotificationQueue();
    this.modalStateService.openNotificationModal({
      notification,
      behavior: 'replace',
      shouldStore: false,
    });
  }

  /** Same fallbacks as staff orders / vendor redeem (header shop + persisted user location). */
  private resolvePhoneRedeemLocationId(): string {
    return String(
      this.locationService.getShopLocation()?.id ?? getUserDataLocationId() ?? ''
    ).trim();
  }

  private buildPrepaidSuccessDisplayContext(
    option: MultiEligiblePurchaseOption,
    selectedItem: MultiEligiblePrepaidItem | null
  ) {
    const plate = this.plateDisplay !== '—' ? this.plateDisplay : undefined;
    return buildPrepaidRedeemOrderSuccessDisplayContext({
      option,
      selectedItem,
      prepaidStatus: this.prepaidStatus,
      customerFirstName: this.customerFirstName,
      customerLastName: this.customerLastName,
      customerName: this.customerName,
      customerEmail: this.customerEmail,
      customerPhone: this.customerPhone !== '—' ? this.customerPhone : undefined,
      plate,
    });
  }

  private confirmWaySingleWash(option: MultiEligiblePurchaseOption): void {
    const locationId =
      this.mode === 'lpr'
        ? String(
            (this.notification?.data as Record<string, unknown> | undefined)?.['location_id'] ??
              this.resolvePhoneRedeemLocationId()
          ).trim()
        : this.resolvePhoneRedeemLocationId();
    const purchase =
      (option.purchase as WayNormalizedPurchase | null | undefined) ??
      ({
        way_pass_id: option.way_pass_id,
        package_name: option.package_name,
        purchase_type: 'single_wash',
        local_package_id: option.package_id,
        package_id: option.package_id,
        status: option.status,
        plate_number: this.plateDisplay !== '—' ? this.plateDisplay : undefined,
      } as WayNormalizedPurchase);

    const confirmContext: Record<string, unknown> = {
      ...(this.notification?.data ?? {}),
      location_id: locationId,
      locationId,
      purchase,
      customer_id: option.customer_id ?? this.phoneCustomerId ?? undefined,
      vehicle_id: option.vehicle_id ?? this.selectedVehicleId ?? undefined,
      customer_name: this.customerName || undefined,
      customer_phone: this.customerPhone || undefined,
      customer_contact: this.customerPhone || undefined,
    };
    // Phone redeem must not reuse a stale LPR entry id from a prior scan notification.
    if (this.mode === 'phone') {
      delete confirmContext['lpr_entry_id'];
      delete confirmContext['lprEntryId'];
      delete confirmContext['id'];
    }

    const payload = buildConfirmWayPurchaseRequest(confirmContext, purchase);
    if (!payload) {
      this.confirmError = !locationId
        ? 'Select a shop location in the header before redeeming.'
        : 'Missing Way purchase data. Cannot redeem this selection.';
      this.syncView();
      return;
    }

    this.confirmError = '';
    this.isConfirming = true;
    this.wayPurchaseApi
      .confirmWayPurchase(payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
          this.syncView();
        })
      )
      .subscribe({
        next: response => {
          this.modalState.hideModal();
          const successNotification = buildOrderSuccessNotificationFromConfirmWayResponse(response);
          if (successNotification) {
            this.modalStateService.clearNotificationQueue();
            this.modalStateService.openNotificationModal({
              notification: successNotification,
              behavior: 'replace',
              shouldStore: false,
            });
          }
        },
        error: (error: unknown) => {
          this.confirmError = extractErrorMessage(
            error,
            'Failed to redeem Way single wash. Please try again.'
          );
          this.syncView();
        },
      });
  }

  private confirmPhoneSingleWash(option: MultiEligiblePurchaseOption): void {
    const locationId = this.resolvePhoneRedeemLocationId();
    const laneId = getUserDataLaneId();
    const payload = buildPhoneSingleWashRedeemPayload({
      option,
      locationId,
      laneId: laneId || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    if (!payload) {
      this.confirmError = !locationId
        ? 'Select a shop location in the header before redeeming.'
        : 'Missing single wash order, customer, or vehicle for redeem.';
      this.syncView();
      return;
    }

    this.confirmError = '';
    this.isConfirming = true;
    this.apiService
      .post('order/phone-single-wash-redeem', payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
          this.syncView();
        })
      )
      .subscribe({
        next: response => {
          this.ordersService.refreshOrders({ resetPage: true });
          this.modalState.hideModal();
          const successNotification = buildNotificationFromMultiEligibleConfirmResponse(
            response,
            'order-success'
          );
          if (successNotification) {
            this.modalStateService.clearNotificationQueue();
            this.modalStateService.openNotificationModal({
              notification: successNotification,
              behavior: 'replace',
              shouldStore: false,
            });
          }
        },
        error: (error: unknown) => {
          this.confirmError = extractErrorMessage(
            error,
            'Failed to redeem single wash. Please try again.'
          );
          this.syncView();
        },
      });
  }

  private confirmPhoneMembership(option: MultiEligiblePurchaseOption): void {
    const locationId = this.resolvePhoneRedeemLocationId();
    const laneId = getUserDataLaneId();
    const payload = buildPhoneMembershipRedeemPayload({
      option,
      locationId,
      laneId: laneId || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    if (!payload) {
      this.confirmError = !locationId
        ? 'Select a shop location in the header before redeeming.'
        : 'Missing membership order, customer, or vehicle for redeem.';
      this.syncView();
      return;
    }

    this.confirmError = '';
    this.isConfirming = true;
    this.apiService
      .post('order/phone-membership-redeem', payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isConfirming = false;
          this.syncView();
        })
      )
      .subscribe({
        next: _response => {
          this.ordersService.refreshOrders({ resetPage: true });
          this.modalState.hideModal();
          this.showMembershipRedeemSuccessToast(option);
        },
        error: (error: unknown) => {
          this.confirmError = extractErrorMessage(
            error,
            'Failed to redeem membership wash. Please try again.'
          );
          this.syncView();
        },
      });
  }

  /** Membership redeem from this chooser already confirmed — toast instead of Active subscription. */
  private showMembershipRedeemSuccessToast(option?: MultiEligiblePurchaseOption | null): void {
    const packageName = String(option?.package_name ?? '').trim();
    this.toastService.add({
      severity: 'success',
      summary: 'Membership redeemed',
      detail: packageName
        ? `${packageName} redeemed. Please proceed to the wash bay.`
        : 'Subscription redeemed. Please proceed to the wash bay.',
    });
  }

  toggleHistory(): void {
    this.historyExpanded = !this.historyExpanded;
    this.syncView();
  }

  goToHistoryPage(page: number): void {
    const { vehicleId } = this.resolveHistoryIds();
    if (!vehicleId || !this.historyBanner) {
      return;
    }
    const hp = this.historyBanner.historyPagination;
    const target = Math.max(1, Math.min(page, hp?.totalPages ?? page));
    if (hp && target === hp.page) {
      return;
    }
    this.fetchHistoryPage(target, {
      buildInsight: false,
      contextKey: this.historyContextKey ?? this.currentHistoryContextKey() ?? undefined,
    });
  }

  onHistoryPageSizeChange(limit: number): void {
    const next = Math.floor(Number(limit));
    if (!Number.isFinite(next) || next < 1) {
      return;
    }
    if (next === this.historyService.pageLimit) {
      return;
    }
    this.historyService.setPageLimit(next);
    const { vehicleId } = this.resolveHistoryIds();
    if (vehicleId) {
      this.historyService.invalidateForVehicle(vehicleId);
    }
    this.fetchHistoryPage(1, {
      buildInsight: false,
      contextKey: this.historyContextKey ?? this.currentHistoryContextKey() ?? undefined,
    });
  }

  openRewashReasonModal(target?: OrderRequestRewashTarget | null): void {
    const normalized = normalizeRewashTarget(target);
    if (!normalized) {
      return;
    }
    this.rewashTarget = normalized;
    this.rewashReasonModalOpen = true;
    this.syncView();
  }

  closeRewashReasonModal(): void {
    this.rewashReasonModalOpen = false;
    this.rewashTarget = null;
    this.syncView();
  }

  onRewashConfirmed(result: CreateRewashOrderResult): void {
    this.closeRewashReasonModal();
    this.rewashEligibility = null;
    this.membershipRewashEligibility = null;
    this.prepaidRewashEligibility = null;
    this.ordersService.refreshOrders();
    this.toastService.add({
      severity: 'success',
      summary: result.created ? 'Rewash created' : 'Rewash already exists',
      detail: result.created
        ? 'The rewash order was created successfully.'
        : 'An active rewash order already exists for this wash.',
    });
    this.modalState.hideModal();
  }

  /** customer_id / vehicle_id from selected purchase option, with phone / plate / LPR fallbacks. */
  private resolveHistoryIds(): { customerId: string; vehicleId: string } {
    const nd = (this.notification?.data ?? {}) as Record<string, any>;
    const customerId = String(
      this.selectedOption?.customer_id ??
        this.phoneCustomerId ??
        nd['customer_id'] ??
        nd['customerId'] ??
        ''
    ).trim();
    const vehicleId = String(
      this.selectedOption?.vehicle_id ??
        this.selectedVehicleId ??
        nd['vehicle_id'] ??
        nd['vehicleId'] ??
        ''
    ).trim();
    return { customerId, vehicleId };
  }

  private ensureWashHistory(options?: { force?: boolean }): void {
    const { customerId, vehicleId } = this.resolveHistoryIds();
    const contextKey = this.historyService.buildContextKey(customerId, vehicleId);
    if (!contextKey) {
      this.clearWashHistoryState();
      return;
    }

    if (options?.force) {
      this.historyService.invalidateForVehicle(vehicleId);
      this.historyBanner = null;
      this.rewashEligibility = null;
      this.membershipRewashEligibility = null;
      this.prepaidRewashEligibility = null;
      this.rewashTarget = null;
      this.historyContextKey = null;
      this.historyBuildInFlightKey = null;
    }

    if (this.historyBuildInFlightKey === contextKey && !options?.force) {
      return;
    }
    if (
      !options?.force &&
      this.historyContextKey === contextKey &&
      !!this.historyBanner &&
      !this.isLoadingHistory
    ) {
      return;
    }

    if (!options?.force && this.restoreHistoryFromCache(contextKey)) {
      this.historyContextKey = contextKey;
      this.syncView();
      return;
    }

    if (this.historyContextKey !== contextKey) {
      this.historyBanner = null;
      // Keep LPR-seeded rewash eligibility until history returns (may omit keys).
      this.rewashTarget = null;
      this.historyExpanded = false;
    }

    this.historyBuildInFlightKey = contextKey;
    this.fetchHistoryPage(1, { buildInsight: true, contextKey });
  }

  private fetchHistoryPage(
    page: number,
    options: { buildInsight: boolean; contextKey?: string }
  ): void {
    const { vehicleId } = this.resolveHistoryIds();
    if (!vehicleId) {
      return;
    }

    const skipReason = this.historyService.peekFetchSkip(vehicleId, page, options.buildInsight);
    if (skipReason) {
      if (skipReason === 'cooldown' && options.buildInsight) {
        this.isLoadingHistory = false;
        const cacheKey = options.contextKey ?? this.currentHistoryContextKey();
        if (cacheKey) {
          this.restoreHistoryFromCache(cacheKey);
        }
        this.syncView();
      }
      return;
    }

    this.isLoadingHistory = true;
    this.historyService
      .fetchVehicleOrderHistoryPage(vehicleId, page, options.buildInsight)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          if (result.skipped) {
            this.isLoadingHistory = false;
            if (
              options.buildInsight &&
              options.contextKey &&
              this.historyBuildInFlightKey === options.contextKey
            ) {
              this.historyBuildInFlightKey = null;
            }
            if (options.buildInsight && result.skipReason === 'cooldown') {
              const cacheKey = options.contextKey ?? this.currentHistoryContextKey();
              if (cacheKey) {
                this.restoreHistoryFromCache(cacheKey);
              }
            }
            this.syncView();
            return;
          }

          this.isLoadingHistory = false;
          if (
            options.buildInsight &&
            options.contextKey &&
            this.historyBuildInFlightKey === options.contextKey
          ) {
            this.historyBuildInFlightKey = null;
          }

          const response = result.response;
          if (!response?.status) {
            this.syncView();
            return;
          }

          if (options.buildInsight && options.contextKey) {
            const current = this.currentHistoryContextKey();
            if (current && current !== options.contextKey) {
              this.syncView();
              return;
            }
          }

          const rows = Array.isArray(response.data) ? response.data : [];
          if (!options.buildInsight) {
            const current = this.currentHistoryContextKey();
            if (options.contextKey && current && current !== options.contextKey) {
              this.syncView();
              return;
            }
            if (this.historyBanner) {
              this.historyBanner = patchOrderRequestHistoryBannerPage(
                this.historyBanner,
                rows,
                response,
                page,
                this.historyService.pageLimit
              );
            }
            this.syncView();
            return;
          }

          if (options.contextKey) {
            this.historyContextKey = options.contextKey;
          }

          const insight = buildOrderRequestHistoryInsight(
            response,
            page,
            this.historyService.pageLimit
          );
          this.historyBanner = insight.banner;
          // Keep LPR-seeded eligibility when history payload omits these keys.
          if (response?.rewash_eligibility !== undefined) {
            this.rewashEligibility = parseRewashEligibilityFromHistoryResponse(response);
          }
          if (response?.membership_rewash_eligibility !== undefined) {
            this.membershipRewashEligibility =
              parseMembershipRewashEligibilityFromHistoryResponse(response);
          }
          if (response?.prepaid_rewash_eligibility !== undefined) {
            this.prepaidRewashEligibility =
              parsePrepaidRewashEligibilityFromHistoryResponse(response);
          }
          if (options.contextKey) {
            this.historyService.saveInsightCache(
              options.contextKey,
              insight.banner,
              insight.suggestion,
              this.rewashEligibility,
              this.membershipRewashEligibility,
              this.prepaidRewashEligibility
            );
          }
          this.syncView();
        },
        error: () => {
          this.isLoadingHistory = false;
          if (
            options.buildInsight &&
            options.contextKey &&
            this.historyBuildInFlightKey === options.contextKey
          ) {
            this.historyBuildInFlightKey = null;
          }
          this.syncView();
        },
      });
  }

  private currentHistoryContextKey(): string | null {
    const { customerId, vehicleId } = this.resolveHistoryIds();
    return this.historyService.buildContextKey(customerId, vehicleId);
  }

  private restoreHistoryFromCache(contextKey: string): boolean {
    const cached = this.historyService.restoreInsightCache(contextKey);
    if (!cached) {
      return false;
    }
    this.historyBanner = cached.banner;
    // Keep LPR-seeded eligibility from the current event over a prior cache entry.
    if (this.rewashEligibility == null) {
      this.rewashEligibility = cached.rewashEligibility;
    }
    if (this.membershipRewashEligibility == null) {
      this.membershipRewashEligibility = cached.membershipRewashEligibility;
    }
    if (this.prepaidRewashEligibility == null) {
      this.prepaidRewashEligibility = cached.prepaidRewashEligibility;
    }
    this.isLoadingHistory = false;
    return true;
  }

  private lprEntryIdFromNotification(notification: NotificationData | null): string {
    const data = (notification?.data ?? {}) as Record<string, unknown>;
    return String(data['lpr_entry_id'] ?? data['lprEntryId'] ?? '').trim();
  }

  private dismissLaneAttentionForNotification(notification: NotificationData | null): void {
    this.laneAttentionService.dismissForLprEntry(this.lprEntryIdFromNotification(notification));
  }

  private clearWashHistoryState(): void {
    this.historyBanner = null;
    this.rewashEligibility = null;
    this.membershipRewashEligibility = null;
    this.prepaidRewashEligibility = null;
    this.rewashTarget = null;
    this.historyExpanded = false;
    this.historyContextKey = null;
    this.historyBuildInFlightKey = null;
    this.isLoadingHistory = false;
    this.rewashReasonModalOpen = false;
  }
}
